<template lang="pug">
v-select.x-widget-rows-per-page.v-btn(
  v-model="content"
  v-bind="$attrs"
  ref="input"
  filled
  solo
  style="max-width: 85px; max-height: 30px;"
  :items="options"
  :class="classes"
  :error-messages="errorMessages"
  :dense="!isLarge"
  :hide-details="hasNoHints"
  :rules="rules"
  :prepend-inner-icon="myIcon"
  :readonly="isReadOnly"
  :required="isRequired && !isDisabled"
  :disabled="isDisabled"
  @keyup.enter="$emit('action-performed', $refs.input)"
)
  template(v-if="useIcon" #item="{ item, on, attrs }")
    v-list-item(
      v-bind="attrs"
      v-on="on"
      ripple
    )
      v-list-item-content
        v-list-item-title
          | {{ item.text }}
      v-list-item-action
        v-icon(small)
          | {{ item.icon }}
</template>

<style>
.x-widget-rows-per-page .v-input__slot {
  padding-right: 0 !important;
}
.x-widget-rows-per-page .v-input__slot {
  min-height: 0 !important;
  max-height: 30px !important;
  height: 30px !important;
}
.x-widget-rows-per-page .v-input__slot fieldset {
  min-height: 0 !important;
  max-height: 35px !important;
  height: 35px !important;
}
.x-widget-rows-per-page .v-input__slot .v-select__slot {
  min-height: 0 !important;
  max-height: 35px !important;
  height: 29px !important;
}
</style>

<script>
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import { interpolate, isInterpolated } from '@/helpers/strings'

function defaultOptions() {
  return [
    { value: 10, text: '10' },
    { value: 20, text: '20' },
    { value: 30, text: '30' },
    { value: 50, text: '50' },
    { value: 100, text: '100' },
    { value: 200, text: '200' },
    { value: 300, text: '300' },
    { value: 500, text: '500' },
    { value: 1000, text: '1000' },
    { value: 2000, text: '2000' },
    { value: 3000, text: '3000' },
    { value: 5000, text: '5000' },
    { value: 10000, text: '10000' },
    { value: 20000, text: '20000' },
    { value: 30000, text: '30000' },
    { value: 50000, text: '50000' },
  ]
}

export default {

  props: {
    /**
     * Objeto contendo os valores e campos do contexto no qual o widget está
     * inserido para inferência de propriedades aplicadas em interpolação de
     * templates de texto.
     */
    context: Object,

    /**
     * Instância opcional de um campo do Paper.
     * @type {Keep.Paper.Design.Field}
     */
    field: Object,

    /**
     * Um dos tipos de dados suportados pelo Paper.
     * @type {Keep.Paper.Design.TypeNames}
     */
    dataType: String,

    /**
     * Diz se o componente suporta seleção de intervalo.
     * @type {Boolean}
     */
    allowRange: Boolean,

    /**
     * Ativa o modo super denso para exibir uma caixa de seleção do tamanho de um botão denso.
     * @type {Boolean}
     */
    superDense: Boolean,

    /**
     * Valor editado no componente.
     * @type {Any}
     */
    value: {
      validator: v => true
    },
  },

  data: () => ({
    icons: {
      mdiEyeOutline,
      mdiEyeOffOutline,
    },

    dataSet: null,  // Um objeto do tipo {Keep.Paper.Design.DataSet}
    failure: null,  // Um objeto do tipo {Keep.Paper.Design.Status}
  }),

  computed: {
    myDescription: vm => vm.getIt('description'),
    myHint: vm => vm.getIt('hint'),
    isLarge: vm => vm.hasIt('large'),
    hasNoHints: vm => vm.hasIt('noHints'),
    isDisabled: vm => vm.hasIt('disabled'),
    isRequired: vm => vm.hasIt('required'),
    isReadOnly: vm => vm.hasIt('readOnly') || vm.hasIt('readonly'),
    myIcon: vm => vm.getIt('icon'),
    myFont: vm => vm.getIt('font'),

    errorMessages () {
      return this.failure
        ? [ 'Não foi possível carregar os dados.', this.failure.message ]
        : null
    },

    options() {
      const sets = this.dataSet?.sets
      if (!sets) return defaultOptions()

      const key = this.getIt('set')
      const set = key
        ? sets.filter(s => s.name === key)[0]
        : sets.filter(s => !s.name || s.name.toLowerCase() === 'default')[0]
      if (!set || !set.rows) return defaultOptions()

      const rows = set.rows

      const items = rows.map(row => {
        const keys = Object.keys(row).filter(key => !key.startsWith('@'))
        const valueKey = keys[0]
        const textKey = keys[1] || valueKey
        const icon = row.icon?.value
        const item = { value: row[valueKey], text: row[textKey], icon }
        if (item.value.value) item.value = item.value.value
        if (item.text.value) item.text = item.text.value
        return item
      })

      return items
    },

    useIcon () {
      return this.options.some(opt => !!opt.icon)
    },

    classes () {
      const classes = {}

      classes['x-widget-readonly'] = this.isReadOnly

      // FontStyle:
      //    Default = 0,
      //    Italic = 1,
      //    Bold = 2,
      //    Overline = 4,
      //    Strikethrough = 8,
      //    Underline = 16,
      //    Monospace = 32,
      const font = this.myFont || 0
      if ((font &  1) ==  1) classes['font-italic'] = true
      if ((font &  2) ==  2) classes['font-weight-bold'] = true
      if ((font &  4) ==  4) classes['text-decoration-overline'] = true
      if ((font &  8) ==  8) classes['text-decoration-line-through'] = true
      if ((font & 16) == 16) classes['text-decoration-underline'] = true
      if ((font & 32) == 32) classes['x-font-monospace'] = true

      return classes
    },

    content: {
      get () {
        let selection = this.options.filter(opt => opt.value == this.value)[0]
        if (!selection) {
          selection = this.options.filter(opt => opt.value == 1000)[0]
          if (!selection) {
            // Selection será a última opção da lista.
            selection = this.options[this.options.length - 1]
          }
        }
        const expectedValue = selection?.value || null
        if (this.value != expectedValue) {
          this.$nextTick(() => this.$emit('input', expectedValue))
        }
        return expectedValue
      },
      set (value) {
        this.$emit('input', (value === undefined ? null : value))
      }
    },

    rules () {
      const required = (this.isRequired && !this.isDisabled)
      return [
        v => !required || !!v || 'Campo requerido',
        // v => (v && v.length <= 10) || 'Name must be less than 10 characters',
      ]
    },
  },

  watch: {
    $route () {
      this.loadOptionsAsync()
    },
  },

  mounted () {
    this.loadOptionsAsync()
  },

  methods: {
    getIt (property) {
      return this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
    },
    hasIt (property) {
      if (this.$props[property] === '') return true
      if (this.$attrs[property] === '') return true
      const value = this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
      return !!value
    },

    async loadOptionsAsync () {
      this.dataSet = null
      this.failure = null

      var dataSet = this.getIt('dataSet')
      if (!dataSet?.see) {
        this.dataSet = dataSet
        return
      }

      // Resolvendo a referência ao dataset...
      const result = await this.$resolve(dataSet.see, { noCache: true })
      if (!result.ok) {
        this.failure = result.entity
        return
      }

      this.dataSet = result.entity
    },

    format(text) {
      return isInterpolated(text) ? interpolate(text, this.context) : text
    },
  },
}
</script>